import React, { useCallback, useEffect, useRef, useState } from "react";
import { SheetMusicViewer } from "./SheetMusicViewer";
import { useMidiPlayer } from "../hooks/useMidiPlayer";

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

type SheetViewModalProps = {
  musicXmlUrl: string | null | undefined;
  midiUrl: string | null | undefined;
  originalAudioUrl: string | null;
  duration: string;
  title?: string;
  onClose: () => void;
};

export const SheetViewModal: React.FC<SheetViewModalProps> = ({
  musicXmlUrl,
  midiUrl,
  originalAudioUrl,
  duration,
  title = "Sheet view",
  onClose,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const [playbackMode, setPlaybackMode] = useState<"original" | "midi">(
    originalAudioUrl ? "original" : "midi"
  );
  const midiPlayer = useMidiPlayer(midiUrl ?? null);

  const handlePlayPause = useCallback(() => {
    if (playbackMode === "midi") {
      if (playing) {
        midiPlayer.pause();
        setPlaying(false);
      } else {
        midiPlayer.play().then(() => setPlaying(true));
      }
      return;
    }
    const audio = audioRef.current;
    if (!audio || !originalAudioUrl) return;
    if (playing) {
      audio.pause();
      setPlaying(false);
    } else {
      audio.play().then(() => setPlaying(true));
    }
  }, [originalAudioUrl, playing, playbackMode, midiPlayer]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setPlaying(false);
    const onTimeUpdate = () => setCurrentTime(audio.currentTime);
    const onLoadedMetadata = () => setDurationSec(audio.duration);
    audio.addEventListener("ended", onEnded);
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    return () => {
      audio.removeEventListener("ended", onEnded);
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    if (playbackMode === "midi") {
      audioRef.current?.pause();
      setPlaying(midiPlayer.isPlaying);
      setCurrentTime(midiPlayer.currentTime);
      setDurationSec(midiPlayer.duration);
    }
  }, [playbackMode, midiPlayer.isPlaying, midiPlayer.currentTime, midiPlayer.duration]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const totalSec = durationSec || (duration.includes(":") ? (() => {
    const parts = duration.split(":").map(Number);
    return parts.length >= 2 ? parts[0] * 60 + parts[1] : 0;
  })() : 0);
  const progressPercent = totalSec > 0 ? Math.min(100, (currentTime / totalSec) * 100) : 0;

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  }, [onClose]);

  return (
    <div className="sheet-view-overlay" onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label={title}>
      <div className="sheet-view-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-view-header">
          <h2 className="sheet-view-title">{title}</h2>
          <button
            type="button"
            className="sheet-view-close"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="sheet-view-body">
          <div className="sheet-view-score">
            <SheetMusicViewer musicXmlUrl={musicXmlUrl} />
          </div>
        </div>

        {(originalAudioUrl || midiUrl) && (
          <div className="sheet-view-audio-bar">
            <audio
              ref={audioRef}
              src={originalAudioUrl}
              onEnded={() => setPlaying(false)}
            />
            <div className="sheet-view-audio-mode">
              <button
                type="button"
                className={`sheet-view-audio-mode-btn ${playbackMode === "original" ? "active" : ""}`}
                onClick={() => {
                  if (playbackMode === "midi") {
                    midiPlayer.stop();
                  }
                  setPlaybackMode("original");
                }}
                disabled={!originalAudioUrl}
              >
                Original
              </button>
              <button
                type="button"
                className={`sheet-view-audio-mode-btn ${playbackMode === "midi" ? "active" : ""}`}
                onClick={() => {
                  audioRef.current?.pause();
                  setPlaybackMode("midi");
                }}
                disabled={!midiUrl}
              >
                MIDI synth
              </button>
            </div>
            <button
              type="button"
              className="sheet-view-play"
              onClick={handlePlayPause}
              aria-label={playing ? "Pause" : "Play"}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                {playing ? (
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
                ) : (
                  <path d="M8 5v14l11-7L8 5z" />
                )}
              </svg>
            </button>
            <div className="sheet-view-audio-progress-wrap">
              <div className="sheet-view-audio-progress">
                <div className="sheet-view-audio-progress-fill" style={{ width: `${progressPercent}%` }} />
              </div>
              <div className="sheet-view-audio-time">
                <span>{formatDuration(currentTime)}</span>
                <span className="sheet-view-audio-sep">/</span>
                <span>{totalSec > 0 ? formatDuration(totalSec) : duration}</span>
              </div>
            </div>
            <span className="sheet-view-audio-label">
              {playbackMode === "midi" ? "MIDI synth" : "Original audio"}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
